import { useEffect, useState } from 'react';
import { Bell, X, Settings, ChevronRight } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotificacionesPendientes } from '@/components/hooks/useNotificacionesPendientes';
import { useProfiles } from '@/components/hooks/useProfiles';
import NotificationSettingsDialog from '@/components/pages/dialogs/NotificationSettingsDialog';
import {
  TOUR_ACTIVE_EVENT,
  TOUR_INACTIVE_EVENT,
} from '@/components/hooks/usePageHelp';

type PerfilTipo = 'empresa' | 'trabajador';

// Ruta a la que llevar según el tipo de notificación, y a qué PERFIL
// pertenece esa pantalla (para poder sincronizar localStorage si hay que
// cruzar de panel). cita_asignada/cita_cancelada dependen del panel activo;
// el resto solo los recibe un perfil, así que tienen ruta y perfil fijos.
const RUTA_POR_TIPO: Record<
  string,
  { ruta: string; perfil: PerfilTipo } | { empresa: string; trabajador: string }
> = {
  cita_asignada: {
    empresa: '/panel-empresa/calendar',
    trabajador: '/panel-trabajador/workerCalendar',
  },
  cita_cancelada: {
    empresa: '/panel-empresa/calendar',
    trabajador: '/panel-trabajador/workerCalendar',
  },
  cita_asignada_trabajador: {
    ruta: '/panel-empresa/calendar',
    perfil: 'empresa',
  },
  cita_cancelada_trabajador: {
    ruta: '/panel-empresa/calendar',
    perfil: 'empresa',
  },
  cita_reprogramada: {
    empresa: '/panel-empresa/calendar',
    trabajador: '/panel-trabajador/workerCalendar',
  },
  cita_reprogramada_trabajador: {
    ruta: '/panel-empresa/calendar',
    perfil: 'empresa',
  },
  bono_comprado: {
    ruta: '/panel-empresa/purchasedVouchers',
    perfil: 'empresa',
  },
  ausencia_solicitada: {
    ruta: '/panel-empresa/absences',
    perfil: 'empresa',
  },
  ausencia_respondida: {
    ruta: '/panel-trabajador/workerProfile',
    perfil: 'trabajador',
  },
};

function resolverDestino(
  tipo: string | null,
  pathname: string
): { ruta: string; perfil: PerfilTipo } | null {
  if (!tipo) return null;
  const entry = RUTA_POR_TIPO[tipo];
  if (!entry) return null;
  if ('ruta' in entry) return entry;

  const enTrabajador = pathname.startsWith('/panel-trabajador');
  return enTrabajador
    ? { ruta: entry.trabajador, perfil: 'trabajador' }
    : { ruta: entry.empresa, perfil: 'empresa' };
}

function tiempoRelativo(fechaISO: string) {
  const diffMs = Date.now() - new Date(fechaISO).getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `hace ${min} min`;
  const horas = Math.floor(min / 60);
  if (horas < 24) return `hace ${horas} h`;
  const dias = Math.floor(horas / 24);
  return `hace ${dias} d`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [tourActivo, setTourActivo] = useState(false);
  const { notificaciones, total, marcarLeida, marcarTodasLeidas } =
    useNotificacionesPendientes();
  const { perfiles } = useProfiles();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onActive = () => setTourActivo(true);
    const onInactive = () => setTourActivo(false);
    window.addEventListener(TOUR_ACTIVE_EVENT, onActive);
    window.addEventListener(TOUR_INACTIVE_EVENT, onInactive);
    return () => {
      window.removeEventListener(TOUR_ACTIVE_EVENT, onActive);
      window.removeEventListener(TOUR_INACTIVE_EVENT, onInactive);
    };
  }, []);

  // El dialog de preferencias muestra unos toggles u otros según el panel
  // en el que estés ahora mismo.
  const perfilActivo: PerfilTipo = location.pathname.startsWith('/panel-trabajador')
    ? 'trabajador'
    : 'empresa';

  const handleClickNotificacion = (n: {
    id_notificacion: string;
    tipo: string | null;
    id_cita: string | null;
    fecha_cita: string | null;
  }) => {
    marcarLeida(n.id_notificacion);

    const destino = resolverDestino(n.tipo, location.pathname);
    setOpen(false);
    if (!destino) return;

    const panelActivo = location.pathname.startsWith('/panel-trabajador')
      ? 'trabajador'
      : 'empresa';

    // Si la notificación lleva a un panel DISTINTO al activo, sincroniza
    // id_perfil/id_empresa en localStorage ANTES de navegar — si no, el
    // otro panel carga con el id_perfil equivocado (le pasó con "ausencia
    // aprobada" visto desde el panel de empresa).
    if (destino.perfil !== panelActivo) {
      const idEmpresaActual = localStorage.getItem('id_empresa');
      const perfilDestino = perfiles.find(
        (p: any) =>
          p.perfil === destino.perfil &&
          (!idEmpresaActual || p.id_empresa === idEmpresaActual)
      );

      if (perfilDestino) {
        localStorage.setItem('id_perfil', perfilDestino.id_perfil);
        localStorage.setItem('id_empresa', perfilDestino.id_empresa || '');
      } else {
        console.warn(
          `NotificationBell: no se encontró el perfil "${destino.perfil}" del usuario para navegar; se navega igualmente.`
        );
      }
    }

    // Si la notificación apunta a una cita concreta, se pasan id y fecha:
    // el calendario los usa para ir a ese día, hacer scroll a la hora y
    // resaltarla (ver useCitaDestacada). Sin ellos cae en el día de hoy.
    const ruta =
      n.id_cita && n.fecha_cita
        ? `${destino.ruta}?cita=${n.id_cita}&fecha=${n.fecha_cita}`
        : destino.ruta;

    navigate(ruta);
  };

  if (tourActivo) return null;

  return (
    <>
      {/* Botón fijo, siempre visible arriba a la derecha */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed z-40 right-4 shrink-0 p-2.5 rounded-full bg-white/90 dark:bg-[#1c1c1e]/90 backdrop-blur-sm border border-gray-200 dark:border-white/10 shadow-md hover:bg-white dark:hover:bg-white/10 transition"
        style={{ top: 'calc(env(safe-area-inset-top) + 1rem)' }}
        aria-label="Notificaciones"
      >
        <Bell className="w-5 h-5 text-[#103254] dark:text-white" />
        {total > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-semibold leading-none">
            {total > 9 ? '9+' : total}
          </span>
        )}
      </button>

      {/* Panel deslizante desde la derecha, con overlay */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/20 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.div
              className="fixed inset-y-0 right-0 z-50 w-full sm:max-w-sm bg-white dark:bg-[#1c1c1e] border-l border-gray-200 dark:border-white/10 shadow-2xl flex flex-col"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              style={{ paddingTop: 'env(safe-area-inset-top)' }}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-white/10 shrink-0">
                <span className="text-base font-semibold text-gray-900 dark:text-white">
                  Notificaciones
                </span>
                <div className="flex items-center gap-2">
                  {total > 0 && (
                    <button
                      onClick={marcarTodasLeidas}
                      className="text-xs text-[#288E7F] hover:underline mr-1"
                    >
                      Marcar todas leídas
                    </button>
                  )}
                  <button
                    onClick={() => setOpen(false)}
                    className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
                    aria-label="Cerrar"
                  >
                    <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                {/* Preferencias: fila fija al principio, siempre visible */}
                <button
                  type="button"
                  onClick={() => setSettingsOpen(true)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-100 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Settings className="w-4 h-4 text-[#288E7F] shrink-0" />
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Mis notificaciones
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Elige qué avisos quieres recibir.
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                </button>

                {notificaciones.length === 0 ? (
                  <div className="px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                    No tienes notificaciones nuevas
                  </div>
                ) : (
                  notificaciones.map((n) => (
                    <button
                      key={n.id_notificacion}
                      onClick={() => handleClickNotificacion(n)}
                      className="w-full text-left px-5 py-4 border-b border-gray-100 dark:border-white/5 last:border-0 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {n.titulo}
                        </span>
                        <span className="shrink-0 w-2 h-2 rounded-full bg-[#288E7F] mt-1.5" />
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {n.mensaje}
                      </p>
                      <span className="text-[11px] text-gray-400 dark:text-gray-500 mt-1 block">
                        {tiempoRelativo(n.fecha_creacion)}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <NotificationSettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        perfil={perfilActivo}
      />
    </>
  );
}